import { assertNotEmpty } from '@festina-life/core'
import { RawRadioGroup } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import {
  type FrameOptions,
  useTranslateFrameTypes,
} from '../../hooks/useTranslateFrameTypes'
import {
  type EnumeratedValueFrameFieldValue,
  FrameType,
  isEnumeratedValueFrameFieldValue,
  isNotRelevantField,
  isRangedValueFrameFieldValue,
  type RangedValueFrameFieldValue,
  type SimpleValueSetupField,
  type SimpleValueSetupFieldValue,
} from '../../types/templateModel'
import {
  generateEmptyAttributeExpressionValue,
  generateEmptyEnumeratedFrame,
  generateEmptyFieldValue,
  generateEmptyRangedFrame,
} from '../../uiModels/createEmptyFieldHelpers'
import {
  createLiteralTypeInformation,
  sortEnumOptionsWithOptionalDotPrefix,
} from '../fplusExpression/expressionType'
import { useFieldDocumentation } from '../templateFieldDocumentation/useFieldDocumentation'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'
import { useFieldFrame } from './useFieldFrame'

type Props = {
  field: SimpleValueSetupField
}

class FPlusExpressionValue {}

/**
 * Displays the frame value for a template field
 */
export const TemplateFieldFrame = ({ field }: Props) => {
  const {
    frame,
    shouldRenderAFrame,
    shouldRenderRangedValueFrameExpression,
    shouldRenderEnumeratedValueFrame,
  } = useFieldFrame(field)
  const translate = useTranslate()
  const { isDraftTemplate } = useTemplateContext()
  const frameOptionsWithTranslations = useTranslateFrameTypes()
  const { values } = useFieldDocumentation(field)

  const fPlusHandlers = useMemo(() => {
    let expression
    let updateFn

    if (frame && isEnumeratedValueFrameFieldValue(frame)) {
      expression = frame.entries?.expression

      updateFn = (nextValue: FPlusExpressionValue) => {
        const fieldValue =
          field.value ??
          (generateEmptyFieldValue({
            field,
          }) as SimpleValueSetupFieldValue)

        return field.update({
          ...fieldValue,
          frame: {
            ...frame,
            entries: {
              ...(frame.entries ??
                generateEmptyAttributeExpressionValue({
                  path: `${fieldValue.path}.frame`,
                  fieldName: 'entries',
                })),
              origin: frame.entries?.path,
              expression: nextValue,
            },
          } satisfies EnumeratedValueFrameFieldValue,
        } satisfies SimpleValueSetupFieldValue)
      }
    } else if (frame && isRangedValueFrameFieldValue(frame)) {
      expression = frame.range?.expression

      updateFn = (nextValue: FPlusExpressionValue) => {
        const fieldValue =
          field.value ??
          (generateEmptyFieldValue({
            field,
          }) as SimpleValueSetupFieldValue)
        return field.update({
          ...fieldValue,
          frame: {
            ...frame,
            range: {
              ...(frame.range ??
                generateEmptyAttributeExpressionValue({
                  path: `${fieldValue.path}.frame`,
                  fieldName: 'range',
                })),
              origin: frame.range?.path,
              expression: nextValue,
            },
          } satisfies RangedValueFrameFieldValue,
        } satisfies SimpleValueSetupFieldValue)
      }
    }

    return {
      value: expression,
      update: updateFn
        ? updateFn
        : () => {
            throw new Error('Unsupported field type for updates')
          },
    }
  }, [field, frame])

  const handleChangedFrameType = (nextValue: FrameOptions) => {
    const emptyFieldValue = generateEmptyFieldValue({
      field,
    }) as SimpleValueSetupFieldValue

    switch (nextValue) {
      case 'none': {
        ;(field.update as (v: SimpleValueSetupFieldValue) => void)({
          ...(field.value ?? emptyFieldValue),
          frame: undefined,
        })
        break
      }
      case FrameType.RangedValueFrame: {
        const emptyRangedFrame = generateEmptyRangedFrame(emptyFieldValue)
        assertNotEmpty(emptyRangedFrame.range)
        ;(field.update as (v: SimpleValueSetupFieldValue) => void)({
          ...(field.value ?? emptyFieldValue),
          frame: {
            ...emptyRangedFrame,
            range: {
              ...emptyRangedFrame.range,
              origin: emptyRangedFrame.origin,
              ...(fPlusHandlers.value
                ? {
                    expression: {
                      ...fPlusHandlers.value,
                    },
                  }
                : {}),
            },
          },
        })
        break
      }
      case FrameType.EnumeratedValueFrame: {
        const emptyEnumeratedFrame =
          generateEmptyEnumeratedFrame(emptyFieldValue)
        assertNotEmpty(emptyEnumeratedFrame.entries)
        ;(field.update as (v: SimpleValueSetupFieldValue) => void)({
          ...(field.value ?? emptyFieldValue),
          frame: {
            ...emptyEnumeratedFrame,
            entries: {
              ...emptyEnumeratedFrame.entries,
              origin: emptyEnumeratedFrame.origin,
              ...(fPlusHandlers.value
                ? {
                    expression: {
                      ...fPlusHandlers.value,
                    },
                  }
                : {}),
            },
          },
        })
        break
      }
      default:
        throw new Error('Unsupported frame type')
    }
  }

  if (!shouldRenderAFrame && !isDraftTemplate) {
    return <span>-</span>
  }

  const fullEnumPath = `${field.path}.frame.entries`
  const fullRangePath = `${field.path}.frame.range`
  const enumeratedFrameOrigin =
    !!field.value?.frame && isEnumeratedValueFrameFieldValue(field.value.frame)
      ? field.value?.frame.entries?.origin
      : undefined

  const rangedFrameOrigin =
    !!field.value?.frame && isRangedValueFrameFieldValue(field.value.frame)
      ? field.value?.frame.range?.origin
      : undefined
  const frameOrigin = enumeratedFrameOrigin ?? rangedFrameOrigin
  const enumOptions = sortEnumOptionsWithOptionalDotPrefix({
    options: values ?? [],
  })

  return (
    <>
      {isDraftTemplate && (
        <div className="flex flex-row items-center gap-x-4">
          <RawRadioGroup
            options={
              [
                FrameType.EnumeratedValueFrame,
                FrameType.RangedValueFrame,
                'none',
              ] as const
            }
            renderFn={(value) => frameOptionsWithTranslations[value]}
            value={frame?.type ?? 'none'}
            onChange={handleChangedFrameType}
            className="mb-2 w-fit"
            isDisabled={isNotRelevantField(field)}
          />
        </div>
      )}
      {shouldRenderEnumeratedValueFrame && (
        <TemplateFieldExpression
          key={fullEnumPath}
          expression={fPlusHandlers.value}
          onChange={fPlusHandlers.update}
          path={fullEnumPath}
          fallbackCode=""
          isExpanded
          label={translate('templateViewer.fieldDetailsModal.field.frame.enum')}
          typeInformation={createLiteralTypeInformation({
            literalType: 'enum',
            collectionType: 'set',
            enumOptions,
            source: fPlusHandlers.value?.fplusSource,
          })}
          fromOrigin={frameOrigin}
        />
      )}
      {shouldRenderRangedValueFrameExpression && (
        <div className="flex flex-col gap-y-2">
          <TemplateFieldExpression
            key={fullRangePath}
            isExpanded
            expression={fPlusHandlers.value}
            onChange={fPlusHandlers.update}
            path={fullRangePath}
            fallbackCode=""
            label={translate(
              'templateViewer.fieldDetailsModal.field.frame.range',
            )}
            fromOrigin={frameOrigin}
          />
        </div>
      )}
    </>
  )
}
